import React, { useState } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Divider, Switch, NumberInput,
  NumberInputField, useToast, Icon, Link,
} from '@chakra-ui/react';
import { FiFile } from 'react-icons/fi';
import MapSelector from '../../../components/MapSelector';
import deportesApi, { Torneo, ArchivoGenerico } from '../../../api/deportesApi';
import {
  SectionTitle, Field, useCatalogos, fmtFecha, ROJO, ROJO_HOVER, API_UPLOADS,
} from '../_shared';

const esImagenArchivo = (a: ArchivoGenerico) =>
  (a.mimeType || '').startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes((a.tipoArchivo || '').toLowerCase());

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: Torneo;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

export const ESTATUS_TORNEO = ['Programado', 'En proceso', 'Finalizado', 'Cancelado'];

const FormularioTorneo: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const { disciplinas, categorias, ramas, localidades } = useCatalogos();

  const [f, setF] = useState<any>({
    nombre: data?.nombre || '',
    disciplinaId: data?.disciplinaId ? String(data.disciplinaId) : '',
    categoriaId: data?.categoriaId ? String(data.categoriaId) : '',
    ramaId: data?.ramaId ? String(data.ramaId) : '',
    fechaInicio: data?.fechaInicio ? data.fechaInicio.split('T')[0] : (modo === 'new' ? new Date().toISOString().split('T')[0] : ''),
    fechaConclusion: data?.fechaConclusion ? data.fechaConclusion.split('T')[0] : '',
    responsable: data?.responsable || '',
    descripcion: data?.descripcion || '',
    puntosVictoria: data?.puntosVictoria ?? 3,
    puntosEmpate: data?.puntosEmpate ?? 1,
    puntosDerrota: data?.puntosDerrota ?? 0,
    calculaPosiciones: data?.calculaPosiciones ?? true,
    sede: data?.sede || '',
    estado: data?.estado || 'Tlaxcala',
    municipio: data?.municipio || 'Totolac',
    localidadId: data?.localidadId ? String(data.localidadId) : '',
    localidad: data?.localidad || '',
    colonia: data?.colonia || '',
    codigoPostal: data?.codigoPostal || '',
    tipoVialidad: data?.tipoVialidad || '',
    vialidad: data?.vialidad || '',
    numeroExterior: data?.numeroExterior || '',
    numeroInterior: data?.numeroInterior || '',
    referencias: data?.referencias || '',
    latitud: data?.latitud ?? '',
    longitud: data?.longitud ?? '',
    urlMaps: data?.urlMaps || '',
    estatus: data?.estatus || 'Programado',
  });
  const [guardando, setGuardando] = useState(false);

  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));

  const handleLocalidad = (id: string) => {
    const loc = localidades.find((l: any) => String(l.id) === id);
    set('localidadId', id);
    set('localidad', loc?.descripcion || '');
  };

  const handleUbicacion = (lat: number, lng: number, address: any) => {
    setF((p: any) => ({
      ...p,
      latitud: lat,
      longitud: lng,
      urlMaps: 'https://www.google.com/maps?q=' + lat + ',' + lng,
      colonia: p.colonia || address?.neighborhood || '',
      codigoPostal: p.codigoPostal || address?.postalCode || '',
    }));
  };

  const handleGuardar = async () => {
    if (!f.nombre || !f.disciplinaId) {
      toast({ title: 'Campos obligatorios', description: 'Nombre y disciplina son requeridos.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload = {
        ...f,
        disciplinaId: Number(f.disciplinaId),
        categoriaId: f.categoriaId ? Number(f.categoriaId) : null,
        ramaId: f.ramaId ? Number(f.ramaId) : null,
        localidadId: f.localidadId ? Number(f.localidadId) : null,
        puntosVictoria: Number(f.puntosVictoria),
        puntosEmpate: Number(f.puntosEmpate),
        puntosDerrota: Number(f.puntosDerrota),
        latitud: f.latitud === '' ? null : Number(f.latitud),
        longitud: f.longitud === '' ? null : Number(f.longitud),
      };
      if (modo === 'edit' && data) await deportesApi.actualizarTorneo(data.id, payload);
      else await deportesApi.crearTorneo(payload);
      toast({ title: modo === 'edit' ? 'Torneo actualizado' : 'Torneo registrado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  // ────────── MODO VISTA ──────────
  if (modo === 'view') {
    const d = data as Torneo;
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Información general</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Box gridColumn={{ md: 'span 2' }}><Field label="NOMBRE" value={d.nombre} /></Box>
          <Field label="DISCIPLINA" value={d.disciplina?.nombre} />
          <Field label="CATEGORÍA" value={d.categoria?.nombre} />
          <Field label="RAMA" value={d.rama?.nombre} />
          <Field label="ESTATUS" value={<Badge colorScheme="cyan">{d.estatus}</Badge>} />
          <Field label="FECHA INICIO" value={fmtFecha(d.fechaInicio)} />
          <Field label="FECHA CONCLUSIÓN" value={fmtFecha(d.fechaConclusion)} />
          <Field label="RESPONSABLE" value={d.responsable} />
        </SimpleGrid>
        {d.descripcion && (<><SectionTitle>Descripción</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.descripcion}</Text></>)}

        <SectionTitle>Reglas de puntuación</SectionTitle>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Field label="VICTORIA" value={d.puntosVictoria} />
          <Field label="EMPATE" value={d.puntosEmpate} />
          <Field label="DERROTA" value={d.puntosDerrota} />
          <Field label="TABLA" value={d.calculaPosiciones ? 'Calculada' : 'Manual'} />
        </SimpleGrid>

        <SectionTitle>Sede / Ubicación</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="SEDE" value={d.sede} />
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="COLONIA" value={d.colonia} />
          <Field label="C.P." value={d.codigoPostal} />
          <Field label="VIALIDAD" value={[d.tipoVialidad, d.vialidad, d.numeroExterior].filter(Boolean).join(' ')} />
          <Field label="REFERENCIAS" value={d.referencias} />
          {(d.urlMaps || (d.latitud != null && d.longitud != null)) && (
            <Field
              label="UBICACIÓN"
              value={
                <Link href={d.urlMaps || `https://www.google.com/maps?q=${d.latitud},${d.longitud}`} isExternal color="blue.500">
                  Ver en Google Maps
                </Link>
              }
            />
          )}
        </SimpleGrid>

        <SectionTitle>Evidencias</SectionTitle>
        {d.archivos && d.archivos.length > 0 ? (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            {d.archivos.map((a) => (
              <Box key={a.id} borderWidth="1px" borderRadius="md" p={2}>
                {esImagenArchivo(a) ? (
                  <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal title="Abrir evidencia en pestaña nueva">
                    <img src={`${API_UPLOADS}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} />
                  </Link>
                ) : (
                  <VStack py={4}><Icon as={FiFile} boxSize={7} color="red.500" /></VStack>
                )}
                <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal fontSize="2xs" color="blue.500" noOfLines={1}>{a.nombreArchivo}</Link>
              </Box>
            ))}
          </SimpleGrid>
        ) : (
          <Text fontSize="sm" color="gray.400">Sin evidencias registradas.</Text>
        )}
      </VStack>
    );
  }

  // ────────── MODO NUEVO / EDITAR ──────────
  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Información general</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre del torneo</FormLabel>
          <Input value={f.nombre} onChange={(e) => set('nombre', e.target.value)} placeholder="Ej. Copa Municipal de Fútbol 2026" />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Disciplina</FormLabel>
          <Select placeholder="Selecciona" value={f.disciplinaId} onChange={(e) => set('disciplinaId', e.target.value)}>
            {disciplinas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Categoría</FormLabel>
          <Select placeholder="Selecciona" value={f.categoriaId} onChange={(e) => set('categoriaId', e.target.value)}>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Rama</FormLabel>
          <Select placeholder="Selecciona" value={f.ramaId} onChange={(e) => set('ramaId', e.target.value)}>
            {ramas.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_TORNEO.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha inicio</FormLabel>
          <Input type="date" value={f.fechaInicio} onChange={(e) => set('fechaInicio', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha conclusión</FormLabel>
          <Input type="date" value={f.fechaConclusion} onChange={(e) => set('fechaConclusion', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input value={f.responsable} onChange={(e) => set('responsable', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea rows={2} value={f.descripcion} onChange={(e) => set('descripcion', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Reglas de puntuación</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Puntos por victoria</FormLabel>
          <NumberInput min={0} value={f.puntosVictoria} onChange={(v) => set('puntosVictoria', v)}><NumberInputField /></NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Puntos por empate</FormLabel>
          <NumberInput min={0} value={f.puntosEmpate} onChange={(v) => set('puntosEmpate', v)}><NumberInputField /></NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Puntos por derrota</FormLabel>
          <NumberInput min={0} value={f.puntosDerrota} onChange={(v) => set('puntosDerrota', v)}><NumberInputField /></NumberInput>
        </FormControl>
        <FormControl display="flex" flexDirection="column" justifyContent="center">
          <FormLabel fontSize="sm" mb={1}>Tabla automática</FormLabel>
          <HStack>
            <Switch colorScheme="red" isChecked={f.calculaPosiciones} onChange={(e) => set('calculaPosiciones', e.target.checked)} />
            <Text fontSize="xs" color="gray.500">{f.calculaPosiciones ? 'Calculada' : 'Manual'}</Text>
          </HStack>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Sede / Ubicación</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Sede</FormLabel>
          <Input value={f.sede} onChange={(e) => set('sede', e.target.value)} placeholder="Nombre del recinto / campo" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona" value={f.localidadId} onChange={(e) => handleLocalidad(e.target.value)}>
            {localidades.map((l: any) => <option key={l.id} value={l.id}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Colonia</FormLabel>
          <Input value={f.colonia} onChange={(e) => set('colonia', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo vialidad</FormLabel>
          <Input value={f.tipoVialidad} onChange={(e) => set('tipoVialidad', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Vialidad</FormLabel>
          <Input value={f.vialidad} onChange={(e) => set('vialidad', e.target.value)} />
        </FormControl>
        <HStack>
          <FormControl><FormLabel fontSize="sm">No. Ext.</FormLabel><Input value={f.numeroExterior} onChange={(e) => set('numeroExterior', e.target.value)} /></FormControl>
          <FormControl><FormLabel fontSize="sm">C.P.</FormLabel><Input value={f.codigoPostal} onChange={(e) => set('codigoPostal', e.target.value)} /></FormControl>
        </HStack>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Referencias</FormLabel>
          <Input value={f.referencias} onChange={(e) => set('referencias', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel fontSize="sm">Ubicación en el mapa</FormLabel>
        <MapSelector
          onLocationSelect={handleUbicacion}
          initialLat={f.latitud !== '' && f.latitud != null ? Number(f.latitud) : null}
          initialLng={f.longitud !== '' && f.longitud != null ? Number(f.longitud) : null}
        />
        {f.urlMaps && (
          <Link href={f.urlMaps} isExternal color="blue.500" fontSize="sm">Ver ubicación seleccionada en Google Maps</Link>
        )}
      </FormControl>

      <Divider />
      <HStack justify="flex-end" pt={1} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioTorneo;
